<script lang="ts">
import {
  BRAND,
  BRAND_MARK_PATHS,
  DOCS_LINK_ID,
  publicNavLinks,
  SIGNED_OUT_LINKS,
} from "@doc-cheap/site-chrome";
import { page } from "$app/state";
import { chromeOf } from "$lib/i18n/copy/chrome";
import { splitLocale } from "$lib/i18n/locales";
import { localizeHref } from "$lib/i18n/registry";
import { DOCS_URL } from "$lib/seo/site";
import type { AccountConfirmation } from "./delete-dialog";
import ProfileMenu from "./ProfileMenu.svelte";
import { hasSignedInHint } from "./signed-in-hint";

// The one top bar of the whole app. The brand is always a way home; the middle
// carries the public site's sections on every page, the cabinet included; the
// right side is the account control; inside the cabinet a second row of the
// same header carries the cabinet's own sections. Every target is a plain
// path, so what lands in the address bar is what a person could have typed.
let {
  signedIn,
  accountConfirmation = null,
  reopenDeleteDialog = false,
}: {
  signedIn: boolean;
  accountConfirmation?: AccountConfirmation | null;
  reopenDeleteDialog?: boolean;
} = $props();

const CABINET_ROOT = "/app";

interface BarLink {
  href: string;
  label: string;
  // Whether the client router must hand this navigation to the browser.
  external: boolean;
  testid?: string | undefined;
  // The shared definition's own identifier, carried through so the counted
  // event's name is the section's name rather than a second list of strings
  // that has to be kept equal to this one.
  id: string;
}

// The sections themselves come from the shared definition, which the
// documentation's static build renders into its own copy of this bar. Both
// builds show the same sections in the same order because there is one list,
// not two that have to be kept equal by hand.
//
// Status is in that list rather than only in the footer because the moment a
// person wants it is the moment something looks broken — and somebody who
// cannot tell whether the fault is ours goes looking in the navigation they can
// already see, not in the page they have to scroll to the bottom of.
//
// The documentation entry is the one that leaves this app: same origin, and
// still `external`, because /docs is a separate static build the host serves in
// front of this app, so the client router must hand the navigation to the
// browser rather than try to resolve a route for it.
//
// On a translated page the labels come from that language's copy and each
// section links to its translated version where one exists; the English
// labels are the copy's fallback, so an English page renders as it always did.
const chrome = $derived(chromeOf(page.data));
const locale = $derived(splitLocale(page.url.pathname).locale);

const publicLinks: BarLink[] = $derived(
  publicNavLinks(DOCS_URL).map((link) => ({
    href: localizeHref(link.href, locale),
    label: chrome.nav[link.id as keyof typeof chrome.nav] ?? link.label,
    id: link.id,
    external: link.id === DOCS_LINK_ID,
    testid: link.id === DOCS_LINK_ID ? "docs-link" : undefined,
  })),
);

// The sections a person moves between while working. Issuing a key, topping up
// and changing settings are errands about the account rather than places to
// work — each is done once and then not thought about again — so they live in
// the account menu instead (`ProfileMenu.svelte`).
const cabinetLinks: BarLink[] = [
  { id: "dashboard", href: CABINET_ROOT, label: "Dashboard", external: false },
  { id: "analytics", href: "/app/analytics", label: "Analytics", external: false },
  { id: "upload", href: "/app/upload", label: "Upload", external: false },
];

// The public sections stay in the bar inside the cabinet too. When the cabinet
// replaced them with its own, a signed-in person had no way from the cabinet to
// the pricing, the blog or the documentation except the brand — and the sign-in
// page sends a signed-in visitor straight back to the cabinet — so the site
// looked closed to anyone who had not signed out first.
const inCabinet = $derived(page.url.pathname.startsWith(CABINET_ROOT));

// Whether to offer the cabinet or the sign-in links.
//
// A page rendered per request knows the answer already, and `signedIn` carries
// it. A page built at build time does not — there was no request — so its HTML
// always ships the signed-out side, and this fills the gap in the browser by
// reading the marker the server leaves beside the session cookie. The effect
// runs after every navigation, so signing out swaps the bar back without a
// reload.
//
// The consequence, accepted deliberately: a signed-in visitor landing on a
// static page sees "Sign in" for the moment between first paint and hydration.
// The alternative is rendering every one of those pages per request, which
// costs every visitor a round trip to spare returning ones a flash.
//
// This decides presentation only. The marker is readable and forgeable by
// anyone; nothing behind these links trusts it.
let hintedSignedIn = $state(false);
$effect(() => {
  void page.url.href;
  hintedSignedIn = hasSignedInHint(document.cookie);
});

const showAccount = $derived(signedIn || hintedSignedIn);

// The dashboard lives at the cabinet root, so it is current only on an exact
// match; every other section owns its subtree.
function isCurrent(href: string): boolean {
  if (href === CABINET_ROOT) return page.url.pathname === CABINET_ROOT;
  if (!href.startsWith("/")) return false;
  return page.url.pathname === href || page.url.pathname.startsWith(`${href}/`);
}

// `tap-target` raises these to the house minimum where the pointer is a finger
// and does nothing at all where it is a mouse, so the desktop bar is unchanged
// (app.css).
const linkClass =
  "tap-target rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900";
const currentLinkClass =
  "tap-target rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white";
const ctaClass =
  "tap-target rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700";
</script>

<header class="border-b border-slate-200 bg-white">
  <div class="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-3 px-6 py-4">
    <a
      href={localizeHref(BRAND.href, locale)}
      data-testid="nav-brand"
      class="tap-target flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900"
    >
      <svg
        class="h-6 w-6 text-slate-900"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        {#each BRAND_MARK_PATHS as d (d)}
          <path {d} />
        {/each}
      </svg>
      {BRAND.label}
    </a>

    <nav
      aria-label={chrome.navLabel}
      class="order-last flex w-full flex-wrap items-center gap-1 sm:order-none sm:w-auto sm:flex-1"
    >
      <!--
        `data-analytics-id` is the ONE way a click becomes a counted event: the
        consented layer delegates one listener and reads this attribute, and it
        has no way to read a value off the element (lib/consent/tracker.ts). The
        name is a coordinate — area.element.action — so a report is a place in a
        taxonomy rather than a sentence somebody wrote once, and a name that
        does not fit that shape is simply not sent.
      -->
      {#each publicLinks as link (link.href)}
        <a
          href={link.href}
          rel={link.external ? "external" : undefined}
          data-testid={link.testid}
          data-analytics-id="nav.{link.id}.click"
          aria-current={isCurrent(link.href) ? "page" : undefined}
          class={isCurrent(link.href) ? currentLinkClass : linkClass}
        >
          {link.label}
        </a>
      {/each}

    </nav>

    <div class="ms-auto flex items-center gap-2 sm:ms-0">
      {#if showAccount}
        {#if !inCabinet}
          <a
          href={CABINET_ROOT}
          data-testid="nav-cabinet"
          data-analytics-id="nav.cabinet.click"
          class={ctaClass}>{chrome.cabinet}</a
        >
        {/if}
        <ProfileMenu confirmation={accountConfirmation} reopenDelete={reopenDeleteDialog} />
      {:else}
        <a href={SIGNED_OUT_LINKS.signIn.href} data-testid="nav-signin" class={linkClass}>
          {chrome.signIn}
        </a>
        <a
          href={SIGNED_OUT_LINKS.register.href}
          data-testid="nav-register"
          data-analytics-id="nav.register.click"
          class={ctaClass}
        >
          {chrome.register}
        </a>
      {/if}
    </div>
  </div>

  {#if inCabinet}
    <div class="border-t border-slate-100">
      <nav aria-label="Cabinet" class="mx-auto flex max-w-6xl flex-wrap items-center gap-1 px-6 py-2">
        {#each cabinetLinks as link (link.href)}
          <a
            href={link.href}
            data-analytics-id="nav.{link.id}.click"
            aria-current={isCurrent(link.href) ? "page" : undefined}
            class={isCurrent(link.href) ? currentLinkClass : linkClass}
          >
            {link.label}
          </a>
        {/each}

        <!-- The account menu these three moved into is a dropdown held open by
             client state, so with no scripts it can never open and the pages
             behind it would be reachable only by typing their addresses. Here
             they keep their old place in the bar, which is the only way to
             reach them then. -->
        <noscript>
          <a href="/app/keys" class={linkClass}>API keys</a>
          <a href="/app/top-ups" class={linkClass}>Top up</a>
          <a href="/app/settings" class={linkClass}>Settings</a>
        </noscript>
      </nav>
    </div>
  {/if}
</header>
